import React, { useState } from "react";
import { IoClose } from "react-icons/io5";
import CustomButton from "../../components/common/CustomButton";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

export default function GuestBookingModal({
  isOpen,
  onClose,
  onValidateBooking,
  setEmail,
  email,
  isLoading,
}) {
  const navigate = useNavigate();

  const [mode, setMode] = useState(""); // signup | guest
  const [showEmailStep, setShowEmailStep] = useState(false);
  const [emailError, setEmailError] = useState("");

  if (!isOpen) return null;

  const isValidEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

  const handleContinue = () => {
    if (!mode) {
      toast.error("Please select an option to continue.");
      return;
    }

    if (mode === "signup") {
      navigate("/signup");
      return;
    }

    if (mode === "guest") {
      setShowEmailStep(true);
    }
  };

  const handleGuestContinue = () => {
    if (!isValidEmail(email)) {
      setEmailError("Please enter a valid email address");
      return;
    }

    setEmailError("");
    onValidateBooking(email);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center">
      {/* BACKDROP */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* MODAL */}
      <div className="relative w-full sm:max-w-lg bg-white rounded-t-3xl sm:rounded-3xl shadow-2xl p-6 sm:p-8">
        {/* CLOSE */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2 rounded-full hover:bg-gray-100 transition"
        >
          <IoClose size={22} />
        </button>

        {/* HEADER */}
        <h2 className="text-xl font-semibold text-gray-900">
          Continue Booking
        </h2>

        <p className="text-sm text-gray-600 mt-1">
          You are booking as a guest. Please provide your email or sign up
          first.
        </p>

        {/* STEP 1: RADIO OPTIONS */}
        {!showEmailStep && (
          <>
            <div className="mt-6 space-y-4">
              <label className="flex items-center gap-3 p-4 border rounded-xl cursor-pointer hover:border-toucanTeal transition">
                <input
                  type="radio"
                  name="bookingMode"
                  checked={mode === "signup"}
                  onChange={() => setMode("signup")}
                  className="accent-toucanTeal"
                />
                <div>
                  <p className="text-sm font-medium text-gray-900">Sign Up</p>
                  <p className="text-xs text-gray-500">
                    Create an account to manage bookings
                  </p>
                </div>
              </label>

              <label className="flex items-center gap-3 p-4 border rounded-xl cursor-pointer hover:border-toucanTeal transition">
                <input
                  type="radio"
                  name="bookingMode"
                  checked={mode === "guest"}
                  onChange={() => setMode("guest")}
                  className="accent-toucanTeal"
                />
                <div>
                  <p className="text-sm font-medium text-gray-900">
                    Continue as Guest
                  </p>
                  <p className="text-xs text-gray-500">
                    Quick booking without creating an account
                  </p>
                </div>
              </label>
            </div>

            {/* ACTIONS */}
            <div className="flex justify-end gap-3 mt-8">
              <CustomButton variant="secondary" onClick={onClose}>
                Cancel
              </CustomButton>

              <CustomButton onClick={handleContinue}>Continue</CustomButton>
            </div>
          </>
        )}

        {/* STEP 2: EMAIL INPUT */}
        {showEmailStep && (
          <div className="mt-8 space-y-5">
            <div>
              <label className="text-sm font-medium text-gray-700">
                Email Address
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  setEmailError("");
                }}
                placeholder="you@example.com"
                className={`mt-1 w-full rounded-xl border px-4 py-3 text-sm focus:outline-none focus:ring-2 ${
                  emailError
                    ? "border-red-500 focus:ring-red-400"
                    : "border-gray-300 focus:ring-toucanTeal"
                }`}
              />

              {emailError && (
                <p className="text-xs text-red-500 mt-1">{emailError}</p>
              )}
            </div>

            <div className="flex justify-end gap-3 pt-2">
              <CustomButton
                variant="secondary"
                onClick={() => {
                  setShowEmailStep(false);
                  setEmail("");
                  setEmailError("");
                }}
              >
                Back
              </CustomButton>

              <CustomButton onClick={handleGuestContinue} loading={isLoading}>
                Book Now
              </CustomButton>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
